"use client";
import React, { useMemo } from "react";
import AppContainer from "../appContainer";
import { cn } from "@/lib";
import CoreServicesSectionHeader from "../coreServicesSectionHeader";
import { useTranslations } from "next-intl";
import ContinuousServicesSegment from "../continuousServicesSegment";
import { useGetInquiries } from "@/hooks";
import { getInquiriesAdapter } from "@/lib/adapters/getInquiriesAdapter";
import { FlexCol } from "../ui";
import { useDebounce } from "@uidotdev/usehooks";
import { useSearchStore } from "@/store/searchStore";
import { IServiceCard } from "@/interfaces";
import { useRouter } from "next/navigation";
import { freeServices } from "@/constants";

export function filterServicesByLabel(
  coreServices: IServiceCard[],
  label: string,
): IServiceCard[] {
  const search = label.trim();
  return coreServices
    .map((service) => {
      const matchedServices = service.services.filter((service) =>
        service.label.toLowerCase().includes(search),
      );

      if (matchedServices.length === 0) return null;

      return {
        ...service,
        services: matchedServices,
      };
    })
    .filter(Boolean) as IServiceCard[];
}

function mergeStaticFreeServices(coreServices: IServiceCard[]): IServiceCard[] {
  const dynamicFlags = new Set(
    coreServices.flatMap((card) =>
      card.services.map((service) => service.flag).filter(Boolean),
    ),
  );
  const freeFallbackCards = freeServices
    .map((card) => ({
      ...card,
      services: card.services.filter(
        (service) => !service.flag || !dynamicFlags.has(service.flag),
      ),
    }))
    .filter((card) => card.services.length > 0);

  if (!freeFallbackCards.length) {
    return coreServices;
  }

  const [freeFallbackCard] = freeFallbackCards;
  const freeCardIndex = coreServices.findIndex(
    (card) => card.category === "freeServices",
  );

  if (freeCardIndex === -1) {
    return [...coreServices, ...freeFallbackCards];
  }

  return coreServices.map((card, index) =>
    index === freeCardIndex
      ? {
          ...card,
          services: [...card.services, ...freeFallbackCard.services],
        }
      : card,
  );
}

const ContinuousServicesSection = () => {
  const t = useTranslations();
  const { data: services } = useGetInquiries();
  const { query } = useSearchStore();
  const router = useRouter();
  const debouncedSearchQuery = useDebounce(query, 1000);
  const coreServicesData = useMemo(
    () => (services?.data ? getInquiriesAdapter(services.data) : []),
    [services?.data],
  );
  const allServicesData = useMemo(
    () => mergeStaticFreeServices(coreServicesData),
    [coreServicesData],
  );

  const filteredServices: IServiceCard[] = useMemo(() => {
    if (!query) {
      return allServicesData;
    }
    router.push("#core-services-landing");
    return filterServicesByLabel(allServicesData, query);
  }, [allServicesData, debouncedSearchQuery, query, router]);

  if (!services?.data) {
    return null;
  }

  return (
    <section className={cn("mb-20 lg:mb-30")}>
      <AppContainer className="h-auto max-w-[700px] lg:max-w-[900px] xl:max-w-[1000px]">
        <FlexCol className="mx-auto w-full max-w-[900px]">
          {filteredServices.length > 0 ? (
            <>
              <CoreServicesSectionHeader
                title={query ? "نتیجه ی جستجو" : t("services.title")}
                description={query ? "" : t("services.description")}
                className="max-w-[450px]"
              />
              {filteredServices.map((item, idx) => (
                <ContinuousServicesSegment
                  {...item}
                  category={t(`categories.${item.category}` as any)}
                  anchorId={`service-cat-${item.category}`}
                  key={idx}
                />
              ))}
            </>
          ) : (
            <CoreServicesSectionHeader
              title={"نتیجه ای یافت نشد"}
              description={"کلیدی که وارد کردید در لیست استعلامات موجود نیست."}
            />
          )}
        </FlexCol>
      </AppContainer>
    </section>
  );
};

export default ContinuousServicesSection;
